import * as joint from '@clientio/rappid';

import { BaseLinkVerticeCalculator } from './BaseLinkVerticeCalculator';
import { VerticeCalculatorInput } from './link-adjust-types';
import { FlowchartConfig } from '../../../base/flowchart.config';

export class LinkVerticeCalculatorLimited extends BaseLinkVerticeCalculator {
  constructor() {
    super();
  }

  /**
   * horizontalAdjustmentValue is an additional method which ensures the links are in the subflow
   * when the links are moved via adjustment tool
   */
  horizontalAdjustmentValue(finalYValue: number, subflowPositionY: number, subflowHeight: number): number {
    if (finalYValue > subflowPositionY + subflowHeight) {
      return subflowPositionY + subflowHeight - FlowchartConfig.paper.gridSize;
    } else if (finalYValue < subflowPositionY) {
      return subflowPositionY + FlowchartConfig.paper.gridSize;
    } else {
      return finalYValue;
    }
  }

  /**
   * verticalAdjustmentValue is an additional method which ensures the links are in the subflow
   * when the links are moved via adjustment tool
   */
  verticalAdjustmentValue(finalXValue: number, subflowPositionX: number, subflowWidth: number): number {
    if (finalXValue > subflowPositionX + subflowWidth) {
      return subflowPositionX + subflowWidth - FlowchartConfig.paper.gridSize;
    } else if (finalXValue < subflowPositionX) {
      return subflowPositionX + FlowchartConfig.paper.gridSize;
    } else {
      return finalXValue;
    }
  }

  setFirstHorizontalVertices(
    input: VerticeCalculatorInput,
    newVertices: joint.dia.Point[],
    mouseEvent: MouseEvent,
    verticeIndex: number,
  ): void {
    const sourcePoint = input.sourcePoint;
    const sourceAnchor = input.sourceAnchor;
    const startPoint = input.startPoint;
    const vertices = input.vertices;
    const startMouseY = input.startMouseY;
    const subflowPosition = input.subflowPosition;
    const subflowSize = input.subflowSize;
    const i = verticeIndex;

    newVertices.push({
      x: sourcePoint.x + this.getIrregularMargin(sourceAnchor),
      y: sourcePoint.y,
    });
    newVertices.push({
      x: sourcePoint.x + this.getIrregularMargin(sourceAnchor),
      y: this.horizontalAdjustmentValue(sourcePoint.y + (mouseEvent.clientY - startMouseY), subflowPosition.y, subflowSize.height),
    });

    newVertices.push({
      x: vertices[i].x,
      y: this.horizontalAdjustmentValue(startPoint + (mouseEvent.clientY - startMouseY), subflowPosition.y, subflowSize.height),
    });
  }

  setFirstVerticalVertices(
    input: VerticeCalculatorInput,
    newVertices: joint.dia.Point[],
    mouseEvent: MouseEvent,
    verticeIndex: number,
  ): void {
    const sourcePoint = input.sourcePoint;
    const sourceAnchor = input.sourceAnchor;
    const startPoint = input.startPoint;
    const vertices = input.vertices;
    const startMouseX = input.startMouseX;
    const subflowPosition = input.subflowPosition;
    const subflowSize = input.subflowSize;
    const i = verticeIndex;

    newVertices.push({
      x: sourcePoint.x,
      y: sourcePoint.y + this.getIrregularMargin(sourceAnchor),
    });
    newVertices.push({
      x: this.verticalAdjustmentValue(sourcePoint.x + (mouseEvent.clientX - startMouseX), subflowPosition.x, subflowSize.width),
      y: sourcePoint.y + this.getIrregularMargin(sourceAnchor),
    });
    newVertices.push({
      x: this.verticalAdjustmentValue(startPoint + (mouseEvent.clientX - startMouseX), subflowPosition.x, subflowSize.width),
      y: vertices[i].y,
    });
  }

  setLastHorizontalVertices(input: VerticeCalculatorInput, newVertices: joint.dia.Point[], mouseEvent: MouseEvent, verticeIndex: number) {
    const targetPoint = input.targetPoint;
    const targetAnchor = input.targetAnchor;
    const startPoint = input.startPoint;
    const vertices = input.vertices;
    const startMouseY = input.startMouseY;
    const subflowPosition = input.subflowPosition;
    const subflowSize = input.subflowSize;
    const i = verticeIndex;

    newVertices.push({
      x: vertices[i].x,
      y: this.horizontalAdjustmentValue(startPoint + (mouseEvent.clientY - startMouseY), subflowPosition.y, subflowSize.height),
    });
    newVertices.push({
      x: targetPoint.x + this.getIrregularMargin(targetAnchor),
      y: this.horizontalAdjustmentValue(startPoint + (mouseEvent.clientY - startMouseY), subflowPosition.y, subflowSize.height),
    });
    newVertices.push({
      x: targetPoint.x + this.getIrregularMargin(targetAnchor),
      y: targetPoint.y,
    });
  }

  setLastVerticalVertices(input: VerticeCalculatorInput, newVertices: joint.dia.Point[], mouseEvent: MouseEvent, verticeIndex: number) {
    const targetPoint = input.targetPoint;
    const targetAnchor = input.targetAnchor;
    const startPoint = input.startPoint;
    const vertices = input.vertices;
    const startMouseX = input.startMouseX;
    const subflowPosition = input.subflowPosition;
    const subflowSize = input.subflowSize;
    const i = verticeIndex;

    newVertices.push({
      x: this.verticalAdjustmentValue(startPoint + (mouseEvent.clientX - startMouseX), subflowPosition.x, subflowSize.width),
      y: vertices[i].y,
    });
    newVertices.push({
      x: this.verticalAdjustmentValue(startPoint + (mouseEvent.clientX - startMouseX), subflowPosition.x, subflowSize.width),
      y: targetPoint.y + this.getIrregularMargin(targetAnchor),
    });
    newVertices.push({
      x: targetPoint.x,
      y: targetPoint.y + this.getIrregularMargin(targetAnchor),
    });
  }

  setMidHorizontalVertices(input: VerticeCalculatorInput, newVertices: joint.dia.Point[], mouseEvent: MouseEvent, verticeIndex: number) {
    const relatedVerticeIndex = input.relatedVerticeIndex;
    const startPointPrev = input.startPointPrev;
    const startPoint = input.startPoint;
    const vertices = input.vertices;
    const startMouseY = input.startMouseY;
    const subflowPosition = input.subflowPosition;
    const subflowSize = input.subflowSize;
    const i = verticeIndex;

    if (i === relatedVerticeIndex) {
      newVertices.push({
        x: vertices[i].x,
        y: this.horizontalAdjustmentValue(startPoint + (mouseEvent.clientY - startMouseY), subflowPosition.y, subflowSize.height),
      });
    } else if (i === relatedVerticeIndex - 1) {
      newVertices.push({
        x: vertices[i].x,
        y: this.horizontalAdjustmentValue(startPointPrev + (mouseEvent.clientY - startMouseY), subflowPosition.y, subflowSize.height),
      });
    } else {
      newVertices.push(vertices[i]);
    }
  }

  setMidVerticalVertices(input: VerticeCalculatorInput, newVertices: joint.dia.Point[], mouseEvent: MouseEvent, verticeIndex: number) {
    const relatedVerticeIndex = input.relatedVerticeIndex;
    const startPointPrev = input.startPointPrev;
    const startPoint = input.startPoint;
    const vertices = input.vertices;
    const startMouseX = input.startMouseX;
    const subflowPosition = input.subflowPosition;
    const subflowSize = input.subflowSize;
    const i = verticeIndex;

    if (i === relatedVerticeIndex) {
      newVertices.push({
        x: this.verticalAdjustmentValue(startPoint + (mouseEvent.clientX - startMouseX), subflowPosition.x, subflowSize.width),
        y: vertices[i].y,
      });
    } else if (i === relatedVerticeIndex - 1) {
      newVertices.push({
        x: this.verticalAdjustmentValue(startPointPrev + (mouseEvent.clientX - startMouseX), subflowPosition.x, subflowSize.width),
        y: vertices[i].y,
      });
    } else {
      newVertices.push(vertices[i]);
    }
  }
}
